import * as Headless from '@headlessui/react'
import clsx from 'clsx'

export type RadioCardOption<T extends string> = { value: T; label: string; detail?: string }

/**
 * One choice from a few, as large tap targets: full-width rows (`stack`, e.g. which field) or
 * wrapping chips (`wrap`, e.g. Yes / No). Built on Headless UI's RadioGroup, so it is a real radio
 * group: one tab stop, arrow keys move the choice. Nothing is chosen while `value` is ''.
 *
 * `label` is the question, and names the group. Use it instead of wrapping the group in a Catalyst
 * `Field` with a `Label`: there, Headless UI would give every option the question as its name. Names
 * are set with aria-label (plain text) because Headless UI replaces any aria-labelledby we pass.
 */
export function RadioCards<T extends string>({
  label,
  value,
  options,
  onChange,
  layout = 'stack',
  className,
}: {
  label: string
  value: T | ''
  options: readonly RadioCardOption<T>[]
  onChange: (value: T) => void
  layout?: 'stack' | 'wrap'
  className?: string
}) {
  return (
    <div className={className}>
      <div aria-hidden="true" className="text-base/6 font-medium text-zinc-950 select-none sm:text-sm/6 dark:text-white">
        {label}
      </div>
      <Headless.RadioGroup
        value={value === '' ? null : value}
        onChange={(v: T | null) => v !== null && onChange(v)}
        aria-label={label}
        className={clsx('mt-3', layout === 'stack' ? 'space-y-2' : 'flex flex-wrap gap-2')}
      >
        {options.map((o) => (
          <Headless.Radio
            key={o.value}
            value={o.value}
            aria-label={o.detail ? `${o.label}, ${o.detail}` : o.label}
            className={clsx(
              'ring-1 ring-inset focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500',
              layout === 'stack'
                ? 'flex min-h-14 w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left'
                : 'inline-flex min-h-11 items-center rounded-lg px-4 text-sm/6 font-semibold',
              // Unchosen
              // (hover only tints an unchosen option, or it would wash out the chosen one's dark background)
              'bg-white text-zinc-900 ring-zinc-300 not-data-checked:data-hover:bg-zinc-50 dark:bg-white/5 dark:text-white dark:ring-white/15 dark:not-data-checked:data-hover:bg-white/10',
              // Chosen
              'data-checked:bg-zinc-900 data-checked:text-white data-checked:ring-zinc-900 dark:data-checked:bg-white dark:data-checked:text-zinc-900 dark:data-checked:ring-white'
            )}
          >
            <span className={layout === 'stack' ? 'font-medium' : undefined}>{o.label}</span>
            {o.detail && <span className="text-sm opacity-70">{o.detail}</span>}
          </Headless.Radio>
        ))}
      </Headless.RadioGroup>
    </div>
  )
}
